import { ApolloClient, InMemoryCache, HttpLink } from '@apollo/client';
import { stripIgnoredCharacters } from 'graphql';

const contentfulURL = `${process.env.NEXT_PUBLIC_CONTENTFUL_GRAPHQL_HOST}/${process.env.NEXT_PUBLIC_CONTENTFUL_SPACE_ID}/environments/${process.env.NEXT_PUBLIC_CONTENTFUL_ENVIRONMENT}`;

// Contentful enforces an 8 KB limit on the query (whitespace included). Apollo
// prints queries pretty-formatted, so strip semantically-irrelevant whitespace
// before sending to stay under the limit without dropping fields.
const minifyPrint: HttpLink.Options['print'] = (node, originalPrint) =>
  stripIgnoredCharacters(originalPrint(node));

// Function to create client based on preview mode
export function getApolloClient(preview = false) {
  // Server-only tokens (no NEXT_PUBLIC_ prefix) so they are never inlined
  // into the browser bundle. These clients are only used server-side.
  const accessToken = preview
    ? process.env.NEXT_PUBLIC_CONTENTFUL_PREVIEW_ACCESS_TOKEN
    : process.env.NEXT_PUBLIC_CONTENTFUL_ACCESS_TOKEN;

  return new ApolloClient({
    link: new HttpLink({
      uri: contentfulURL,
      headers: {
        Authorization: `Bearer ${accessToken}`,
      },
      print: minifyPrint,
    }),
    cache: new InMemoryCache(),
  });
}

// Default client for server-side usage (published content only).
// Runs during SSR/RSC — token stays on the server and never ships to the browser.
export const client = new ApolloClient({
  link: new HttpLink({
    uri: contentfulURL,
    headers: {
      Authorization: `Bearer ${process.env.NEXT_PUBLIC_CONTENTFUL_ACCESS_TOKEN}`,
    },
    print: minifyPrint,
  }),
  cache: new InMemoryCache(),
});

// Factory for a browser-side client that talks to the same-origin /api/graphql
// proxy, which attaches the Contentful token server-side. Pass `preview` to
// request draft content (the proxy selects the preview token). No token is
// exposed in the browser, so it never appears in the Network tab.
export function getBrowserClient(preview = false) {
  return new ApolloClient({
    link: new HttpLink({
      uri: preview ? '/api/graphql?preview=true' : '/api/graphql',
      print: minifyPrint,
    }),
    cache: new InMemoryCache(),
  });
}

// Default (published content) browser client for callers that don't need preview.
export const browserClient = getBrowserClient(false);

export default client;
